import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { StatusBadge } from '../components/shared/StatusBadge';
import { EmptyState } from '../components/shared/EmptyState';
import { policiesAPI } from '../lib/api';
import api from '../lib/api';
import { formatDate, truncate } from '../lib/utils';
import { FileText, Plus, Trash2, Edit2, Search, Eye, Check, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const POLICY_CATEGORIES = [
  'Security',
  'Privacy',
  'Access Control',
  'Data Management',
  'Incident Response',
  'Business Continuity',
  'Acceptable Use',
  'Vendor Management',
];

export default function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [notifyingPolicy, setNotifyingPolicy] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifyData, setNotifyData] = useState({
    reviewer_emails: '',
    due_date: '',
    message: ''
  });
  const [sendingNotification, setSendingNotification] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'Security',
    version: '1.0',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await policiesAPI.getAll();
      setPolicies(response.data);
    } catch (error) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (policy = null) => {
    if (policy) {
      setEditingPolicy(policy);
      setFormData({
        title: policy.title,
        content: policy.content,
        category: policy.category,
        version: policy.version,
      });
    } else {
      setEditingPolicy(null);
      setFormData({
        title: '',
        content: '',
        category: 'Security',
        version: '1.0',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingPolicy) {
        await policiesAPI.update(editingPolicy.id, formData);
        toast.success('Policy updated successfully');
      } else {
        await policiesAPI.create(formData);
        toast.success('Policy created successfully');
      }
      setIsDialogOpen(false);
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await policiesAPI.updateStatus(id, status);
      toast.success('Policy status updated');
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAcknowledge = async (id) => {
    try {
      await policiesAPI.acknowledge(id);
      toast.success('Policy acknowledged');
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Already acknowledged');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    try {
      await policiesAPI.delete(id);
      toast.success('Policy deleted');
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setSendingNotification(true);
    
    try {
      const emails = notifyData.reviewer_emails.split(',').map(e => e.trim()).filter(e => e);
      if (emails.length === 0) {
        toast.error('Please enter at least one email address');
        return;
      }
      
      await api.post('/notifications/policy-review', {
        policy_id: notifyingPolicy.id,
        reviewer_emails: emails,
        due_date: notifyData.due_date || null,
        message: notifyData.message || null
      });
      
      toast.success(`Notification sent to ${emails.length} reviewer(s)`);
      setNotifyingPolicy(null);
      setNotifyData({ reviewer_emails: '', due_date: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const filteredPolicies = policies.filter(policy => 
    policy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    policy.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader 
        title="Policies" 
        description="Manage organizational policies and employee acknowledgments"
        actions={
          <Button
            data-testid="add-policy-btn"
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          >
            <Plus size={18} className="mr-2" />
            Create Policy
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-mono text-slate-500 uppercase tracking-wider">Total</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{policies.length}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
            <p className="text-sm font-mono text-emerald-600 uppercase tracking-wider">Active</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">
              {policies.filter(p => p.status === 'active').length}
            </p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-mono text-slate-500 uppercase tracking-wider">Draft</p>
            <p className="text-2xl font-bold text-slate-600 mt-1">
              {policies.filter(p => p.status === 'draft').length}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="policy-search"
              placeholder="Search policies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 rounded-sm"
            />
          </div>
        </div>

        {/* Policies Grid */}
        {filteredPolicies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPolicies.map((policy) => (
              <div 
                key={policy.id}
                className="bg-white border border-slate-200 rounded-sm p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={policy.status} />
                    <span className="text-xs text-slate-400 font-mono">v{policy.version}</span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 text-slate-400 hover:text-slate-600">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setViewingPolicy(policy)}>
                        <Eye size={14} className="mr-2" /> View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleOpenDialog(policy)}>
                        <Edit2 size={14} className="mr-2" /> Edit
                      </DropdownMenuItem>
                      {policy.status === 'active' && (
                        <DropdownMenuItem onClick={() => setNotifyingPolicy(policy)}>
                          <Mail size={14} className="mr-2" /> Notify for Review
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleStatusUpdate(policy.id, 'active')}>
                        Publish
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusUpdate(policy.id, 'archived')}>
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(policy.id)}
                        className="text-red-600"
                      >
                        <Trash2 size={14} className="mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <h3 className="font-secondary font-semibold text-slate-900 mb-1">
                  {policy.title}
                </h3>
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                  {truncate(policy.content, 100)}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <span className="badge badge-neutral text-xs">{policy.category}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      {policy.acknowledgments_count} acks
                    </span>
                    {policy.status === 'active' && (
                      <button
                        data-testid={`acknowledge-policy-${policy.id}`}
                        onClick={() => handleAcknowledge(policy.id)}
                        className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                        title="Acknowledge"
                      >
                        <Check size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={FileText}
            title="No policies created"
            description="Create policies to document your organization's security and compliance procedures."
            action={
              <Button
                data-testid="empty-add-policy-btn"
                onClick={() => handleOpenDialog()}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                <Plus size={18} className="mr-2" />
                Create Your First Policy
              </Button>
            }
          />
        )}
      </div>

      {/* Create/Edit Policy Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary">
              {editingPolicy ? 'Edit Policy' : 'Create Policy'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="policy-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Information Security Policy"
                  required
                  className="mt-1.5 rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="policy-category-select" className="mt-1.5 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POLICY_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Version</Label>
                  <Input
                    data-testid="policy-version-input"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="1.0"
                    className="mt-1.5 rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                data-testid="policy-content-input"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your policy content here. You can include guidelines, procedures, and requirements..."
                rows={12}
                required
                className="mt-1.5 rounded-sm font-mono text-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-policy-btn"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                {submitting ? 'Saving...' : editingPolicy ? 'Update Policy' : 'Create Policy'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Policy Dialog */}
      <Dialog open={!!viewingPolicy} onOpenChange={() => setViewingPolicy(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary">{viewingPolicy?.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-4">
              <StatusBadge status={viewingPolicy?.status} />
              <span className="badge badge-neutral">{viewingPolicy?.category}</span>
              <span className="text-sm text-slate-400 font-mono">v{viewingPolicy?.version}</span>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono">
                {viewingPolicy?.content}
              </pre>
            </div>
            <div className="mt-4 text-sm text-slate-500">
              <p>Last updated: {formatDate(viewingPolicy?.updated_at)}</p>
              <p>{viewingPolicy?.acknowledgments_count} acknowledgments</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notify for Review Dialog */}
      <Dialog open={!!notifyingPolicy} onOpenChange={() => setNotifyingPolicy(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-secondary flex items-center gap-2">
              <Mail size={20} className="text-blue-600" />
              Send Review Notification
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendNotification} className="space-y-4 mt-4">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
              <p className="text-sm font-medium text-slate-900">{notifyingPolicy?.title}</p>
              <p className="text-xs text-slate-500">Version {notifyingPolicy?.version} • {notifyingPolicy?.category}</p>
            </div>

            <div>
              <Label>Reviewer Email(s) *</Label>
              <Input
                data-testid="notify-emails-input"
                value={notifyData.reviewer_emails}
                onChange={(e) => setNotifyData({ ...notifyData, reviewer_emails: e.target.value })}
                placeholder="email1@company.com, email2@company.com"
                required
                className="mt-1.5 rounded-sm"
              />
              <p className="text-xs text-slate-500 mt-1">Separate multiple emails with commas</p>
            </div>

            <div>
              <Label>Due Date (optional)</Label>
              <Input
                data-testid="notify-due-date-input"
                type="date"
                value={notifyData.due_date}
                onChange={(e) => setNotifyData({ ...notifyData, due_date: e.target.value })}
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div>
              <Label>Message (optional)</Label>
              <Textarea
                data-testid="notify-message-input"
                value={notifyData.message}
                onChange={(e) => setNotifyData({ ...notifyData, message: e.target.value })}
                placeholder="Add a personal message to the notification..."
                rows={3}
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
              <p className="text-sm text-amber-700">
                <strong>Note:</strong> Email notifications require Resend API configuration. 
                Without API key, notifications will be logged to the console.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setNotifyingPolicy(null)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="send-notification-btn"
                disabled={sendingNotification}
                className="bg-blue-600 hover:bg-blue-700 rounded-sm"
              >
                {sendingNotification ? 'Sending...' : 'Send Notification'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
